import _ from 'lodash';
import React from 'react';
import styled from '@emotion/styled';
import { NavLink } from 'react-router-dom';

import { Icon } from '../lumen';
import { RoutePatterns } from '../constants';
import buildRoutePath from '../utils/buildRoutePath';
import { encodeFilter, stringify, useSearchParams } from '../search_params';

/* Settings that carry over when switching between analysis views (same list
 * the legacy sidebar preserved) — e.g. the concept count selector. */
const PARAMS_TO_PRESERVE = [
  'search',
  'filter',
  'concepts',
  'drivers_of',
  'drivers_count',
  'prevalent_count'
];

export const ProjectTabs = ({
  workspaceId,
  projectId,
  canManageProject,
  rightSlot
}) => {
  const { searchParams } = useSearchParams();
  const preservedParams = _.pick(searchParams, PARAMS_TO_PRESERVE);
  preservedParams.filter = encodeFilter(preservedParams.filter);
  const search = stringify(preservedParams);

  const buildTo = pattern => ({
    pathname: buildRoutePath(pattern, { workspaceId, projectId }),
    search
  });

  return (
    <Root>
      <TabsList role="tablist">
        <Tab to={buildTo(RoutePatterns.HIGHLIGHTS)}>
          <Icon name="highlighter" size={16} />
          <span>Highlights</span>
        </Tab>
        <Tab to={buildTo(RoutePatterns.VOLUME)}>
          <Icon name="chart-mixed" size={16} />
          <span>Volume</span>
        </Tab>
        <Tab to={buildTo(RoutePatterns.GALAXY)}>
          <Icon name="planet-ringed" size={16} />
          <span>Galaxy</span>
        </Tab>
        <Tab to={buildTo(RoutePatterns.DRIVERS)}>
          <Icon name="gauge-simple-max" size={16} />
          <span>Drivers</span>
        </Tab>
        <Tab to={buildTo(RoutePatterns.SENTIMENT)}>
          <Icon name="face-smile" size={16} />
          <span>Sentiment</span>
        </Tab>
        <Tab to={buildTo(RoutePatterns.DASHBOARD_EMBED)}>
          <Icon name="grid-round-2-plus" size={16} />
          <span>Dashboard</span>
        </Tab>
        {canManageProject && (
          <Tab to={buildTo(RoutePatterns.PROJECT_MANAGEMENT)}>
            <Icon name="wrench" size={16} />
            <span>Project Settings</span>
          </Tab>
        )}
      </TabsList>
      {rightSlot && <RightSlot>{rightSlot}</RightSlot>}
    </Root>
  );
};

const Root = styled.nav`
  display: flex;
  align-items: center;
  gap: 24px;
  width: 100%;
`;

const RightSlot = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TabsList = styled.div`
  display: flex;
  flex: 1 1 0;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.color.slate[100]};
`;

/* Shared tab visuals; NavLink handles the "active" match via URL. */
const tabBase = ({ theme }) => `
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  font-family: ${theme.font.sans};
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: ${theme.color.fg.default};
  text-decoration: none;
  border-bottom: 3px solid transparent;
  transform: translateY(1px);  /* overlap the list's 1px bottom border */

  &:hover {
    color: ${theme.color.primary.base};
    text-decoration: none;
  }
`;

const activeTab = ({ theme }) => `
  color: ${theme.color.primary.base};
  font-weight: 500;
  border-bottom-color: ${theme.color.primary.base};
`;

const Tab = styled(NavLink)`
  ${tabBase}

  &.active {
    ${activeTab}
  }
`;

Tab.defaultProps = { activeClassName: 'active' };
